import {useMutation} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import {useParams} from 'react-router-dom'
import {createOrUpdateGenericInfo} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {
    deviceTypes,
    getObsColors,
    obsColWrapperData,
    observationKeys,
    observationListForm,
} from './Constants'
import {getIndexOfValueInMultiCol, getNewsColor} from './Utils'

//Prime React

const PATH = 'healthRecord'

const ObservationForm = forwardRef(({obsData = [], ...props}, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: () => showForm(),
        hideForm: () => hideForm(),
    }))
    const [visible, setVisible] = useState(false)

    const {user} = useAuth()
    // eslint-disable-next-line no-unused-vars
    const [data, setData] = useState({})
    const [news, setNews] = useState(0)
    const [newsLog, setNewsLog] = useState({})
    const {id: idString} = useParams()
    const patientId = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    const showForm = () => {
        blockOverflownScroll()
        setData({})
        setVisible(true)
    }
    const {translateCommons} = TranslateUtils()

    const hideForm = () => {
        unblockOverflownScroll()
        setData({})
        setVisible(false)
    }

    const getColorByValue = (colorList, dataRange, value, isNews = false) => {
        if (
            !colorList ||
            !Array.isArray(colorList) ||
            !dataRange ||
            !Array.isArray(dataRange) ||
            !value
        ) {
            return ''
        }

        const obsColors = getObsColors()
        if (!isNews) {
            if (obsColors && obsColors.lv1) {
                colorList = colorList.map(x =>
                    x.replace(obsColors.lv1, '#44cc61'),
                )
            }

            const headColor = colorList[0]
            const tailColor = colorList[colorList.length - 1]

            colorList.push(tailColor)
            colorList.unshift(headColor)
        }

        let colorValue = ''
        let colorIndex = getIndexOfValueInMultiCol(dataRange, value)
        if (colorIndex !== null) {
            if (!isNews) {
                colorValue = colorList[colorIndex + 1]
            } else {
                colorValue = colorList[colorIndex]
            }
        }

        return colorValue
    }

    const getValueOfTheColor = color => {
        const obsColors = getObsColors()
        if (obsColors && Object.keys(obsColors).length > 0) {
            let index = null
            for (const key in obsColors) {
                if (obsColors[key] === color) {
                    index = key
                }
            }

            if (index) {
                switch (index) {
                    case 'lv1':
                        return 0
                    case 'lv2':
                        return 1
                    case 'lv3':
                        return 2
                    case 'lv4':
                        return 3
                    default:
                        return 0
                }
            }
        }
    }

    const {mutate} = useMutation(
        data => createOrUpdateGenericInfo(PATH, user?.id, data),
        {
            onSuccess: data => {
                if (data?.id) {
                    showNotification(
                        severityNotification.success,
                        'Observation Created !!',
                    )

                    props?.reload()

                    hideForm()
                    if (!props.patient) {
                        setNews(0)
                    }
                }
            },
        },
    )

    const onChangeNewsValue = useCallback(
        data => {
            let newsObj = {
                [observationKeys.levelOfConsciousness]:
                    data.levelOfConsciousness,
                [observationKeys.heartRate]: data.heartRate,
                [observationKeys.respiration]: data.respiration,
                [observationKeys.oxygen]: data.oxygen,
                [observationKeys.temperature]: data.temperature,
                [observationKeys.bloodPressure]: data.systolic,
            }

            let newsValue = 0
            let holderDialog = []
            let newsLogObj = {}
            for (const key in newsObj) {
                if (newsObj[key]) {
                    if (key === observationKeys.levelOfConsciousness) {
                        const allowedConsciousnessKeys = [
                            translateCommons('voice'),
                            translateCommons('confusion'),
                            translateCommons('unresponsive'),
                            translateCommons('pain'),
                        ]
                        if (allowedConsciousnessKeys.includes(newsObj[key])) {
                            newsValue += 3
                            newsLogObj = {
                                ...newsLogObj,
                                [key]: newsObj[key],
                            }
                        } else {
                            newsValue = 0
                            delete newsLogObj[key]
                        }
                    } else {
                        for (let j = 0; j < obsColWrapperData.length; j++) {
                            if (
                                obsColWrapperData[j].key === key &&
                                obsColWrapperData[j].colors &&
                                obsColWrapperData[j].dataRange
                            ) {
                                const colorValue = getColorByValue(
                                    obsColWrapperData[j].colors,
                                    obsColWrapperData[j].dataRange,
                                    newsObj[key],
                                    true,
                                )

                                const valueOfTheColor =
                                    getValueOfTheColor(colorValue)
                                newsValue += valueOfTheColor
                                if (valueOfTheColor === 3) {
                                    holderDialog.push(key)
                                }
                                newsLogObj = {
                                    ...newsLogObj,
                                    [key]: valueOfTheColor,
                                }
                                break
                            }
                        }
                    }
                } else {
                    newsValue = 0
                    break
                }
            }

            if (newsValue > 0) {
                setNews(newsValue)
                setNewsLog(newsLogObj)
            } else {
                setNews(0)
                setNewsLog({})
            }
        },
        [translateCommons],
    )

    const handleOnSubmit = useCallback(
        async data => {
            let toPostData = {
                ...data,
                userId: patientId,
                news: news,
                newsLog: newsLog,
                device: deviceTypes.computer.value,
                // device: deviceTypes.watch.value,
            }
            mutate(toPostData)
        },
        [mutate, news, patientId, newsLog],
    )

    return (
        <Sidebar
            className="p-sidebar-full"
            visible={visible}
            blockScroll
            onHide={() => setVisible(false)}
            fullScreen>
            <div className="prevent-select" style={{paddingBottom: '120px'}}>
                <PageContentWrapper>
                    <FormHeader
                        header={translateCommons('OBSERVATION FORM')}
                        onHide={() => hideForm()}
                    />
                    <CustomForm
                        onSubmit={handleOnSubmit}
                        layout={formLayout.group}
                        dataList={observationListForm}
                        observation
                        userData={props.userData}
                        obsData={obsData}
                        onChangeCustomValue={onChangeNewsValue}
                    />
                </PageContentWrapper>
            </div>
            {!props.patient && (
                <div
                    style={{position: 'fixed', bottom: 0, left: 0, zIndex: 10}}>
                    <div
                        style={{
                            backgroundColor: getNewsColor(news),
                            borderTopRightRadius: '15px',
                            borderBottomRightRadius: '15px',
                            padding: '20px',
                            textAlign: 'center',
                            color: 'black',
                        }}>
                        <label className="font-bold" style={{fontSize: '20px'}}>
                            {translateCommons('NEWS')}
                        </label>
                        <br />
                        <label className="font-bold" style={{fontSize: '60px'}}>
                            {news}
                        </label>
                    </div>
                </div>
            )}
        </Sidebar>
    )
})

export default ObservationForm
